import type { DecoratedIntegration } from '@/pages/Schedule/hooks/useIntegrations';
import {
  type Calendar,
  IntegrationStatus,
  IntegrationType,
} from '@/types/gql.generated';
import { WRITE_SCOPES } from './auth.lib';

export const getSortedCalendars = (
  integration: DecoratedIntegration
): Calendar[] => {
  return [...integration.calendars].sort((a, b) => {
    if (a.readonly !== b.readonly) {
      return a.readonly ? 1 : -1;
    }
    return a.name.localeCompare(b.name);
  });
};

// Microsoft returns a granted scope either shorthand ("Calendars.ReadWrite") or
// fully qualified ("https://graph.microsoft.com/Calendars.ReadWrite"), and the
// server persists whichever it received verbatim. Compare on the permission
// name so a qualified scope still matches — this mirrors toPermissionName in the
// server's src/services/outlookOauth.ts, which the two repos cannot share.
const toPermissionName = (scope: string): string =>
  scope.split('/').pop() ?? scope;

// Outlook's Calendars.ReadWrite scope grants both read and write, so a single
// write-scope check is sufficient (unlike Google's split read/write scopes).
export const canWrite = (integration: DecoratedIntegration): boolean => {
  const scopes = integration.scope.split(' ').map(toPermissionName);
  return WRITE_SCOPES.every((scope) => scopes.includes(scope));
};

export const isSuitableIntegration = (
  integration: DecoratedIntegration
): boolean => {
  return (
    integration.type === IntegrationType.OutlookCalendar &&
    integration.status === IntegrationStatus.Success &&
    canWrite(integration)
  );
};

export const hasOutlookIntegration = (
  integrations: DecoratedIntegration[]
): boolean => {
  return integrations.some(isSuitableIntegration);
};

export const selectOutlookIntegrations = (
  integrations: DecoratedIntegration[]
): DecoratedIntegration[] => {
  return integrations.filter(
    (item) => item.type === IntegrationType.OutlookCalendar
  );
};
